/* Static legal/info pages — pure content, no API calls. */

const Page = ({ eyebrow, title, children }) => (
  <div className="container-mp pt-12 pb-16">
    <div className="max-w-3xl mx-auto">
      <div className="text-xs uppercase tracking-[0.25em] text-gold-bright mb-2">{eyebrow}</div>
      <h1 className="font-display text-3xl md:text-4xl mb-6">{title}</h1>
      <div className="gold-divider mb-10" />
      <div className="prose-mp space-y-6 text-[hsl(var(--text))] leading-relaxed text-[15px]">
        {children}
      </div>
    </div>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-display text-xl md:text-2xl text-gold-bright mt-8 mb-3 tracking-wide">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="text-[hsl(var(--text-muted))]">{children}</p>
);

export const MentionsLegales = () => (
  <Page eyebrow="✦ Informations légales" title="Mentions légales">
    <H2>Éditeur du site</H2>
    <P>
      Le site <strong>monprecieux.com</strong> est édité par Mon Précieux. L’éditeur peut être
      contacté à l’adresse renseignée sur la page <a href="/contact" className="text-gold-bright underline-offset-4 hover:underline">Contact</a>.
    </P>
    <H2>Hébergement</H2>
    <P>
      Site hébergé par Gandi SAS — 63-65 boulevard Massena, 75013 Paris, France.
    </P>
    <H2>Propriété intellectuelle</H2>
    <P>
      L’ensemble du contenu (textes, identité visuelle, code) est protégé par le droit d’auteur.
      Les noms et logos des marques d’appareils restent la propriété de leurs détenteurs respectifs
      et sont uniquement utilisés à des fins d’identification.
    </P>
    <H2>Partenaire</H2>
    <P>
      Mon Précieux est un comparateur indépendant qui s’appuie sur les prix publics affichés par
      CompaRecycle. Les transactions de reprise sont finalisées directement sur la plateforme
      CompaRecycle et ne transitent jamais par Mon Précieux.
    </P>
  </Page>
);

export const PolitiqueConfidentialite = () => (
  <Page eyebrow="✦ Vie privée" title="Politique de confidentialité">
    <H2>Données collectées</H2>
    <P>
      Aucune création de compte n’est requise pour utiliser Mon Précieux. Lors d’un devis,
      nous enregistrons uniquement les caractéristiques techniques fournies (catégorie, marque,
      modèle, capacité, état) pour calculer l’estimation et améliorer le service. Aucune donnée
      personnelle identifiante n’est demandée.
    </P>
    <H2>Formulaire de contact</H2>
    <P>
      Si vous nous écrivez via la page Contact, nous conservons votre email, votre nom et votre
      message uniquement pour vous répondre. Vous pouvez demander leur suppression à tout moment.
    </P>
    <H2>Vos droits</H2>
    <P>
      Conformément au RGPD, vous disposez d’un droit d’accès, de rectification, d’effacement
      et d’opposition au traitement de vos données. Adressez votre demande via la page Contact.
    </P>
    <H2>Cookies</H2>
    <P>
      Mon Précieux n’utilise pas de cookies publicitaires. Voir notre <a href="/cookies" className="text-gold-bright underline-offset-4 hover:underline">politique cookies</a>.
    </P>
  </Page>
);

export const Cookies = () => (
  <Page eyebrow="✦ Cookies" title="Politique cookies">
    <H2>Cookies essentiels</H2>
    <P>
      Le site utilise un cookie de session technique nécessaire au bon fonctionnement de la
      navigation. Aucun cookie publicitaire ni de profilage commercial n’est déposé.
    </P>
    <H2>Mesure d’audience</H2>
    <P>
      Nous pouvons collecter des statistiques anonymes et agrégées (pages vues, durée) pour
      améliorer l’expérience. Ces données ne permettent pas de vous identifier.
    </P>
    <H2>Vos choix</H2>
    <P>
      Vous pouvez à tout moment configurer votre navigateur pour refuser les cookies. Le site
      restera fonctionnel sans cookies.
    </P>
  </Page>
);

export const CGU = () => (
  <Page eyebrow="✦ Conditions générales" title="Conditions générales d’utilisation">
    <H2>Objet</H2>
    <P>
      Mon Précieux fournit un service d’estimation indicative de la valeur de reprise d’appareils
      électroniques d’occasion. Le service est gratuit, accessible sans inscription, et fonctionne
      en redirigeant l’utilisateur vers la plateforme CompaRecycle pour finaliser la transaction.
    </P>
    <H2>Caractère indicatif de l’estimation</H2>
    <P>
      L’estimation affichée constitue une <strong>indication</strong> calculée à partir des prix
      publiquement affichés par CompaRecycle et de l’état déclaré par l’utilisateur. Le prix
      définitif est déterminé par CompaRecycle après diagnostic complet de l’appareil reçu.
      Mon Précieux ne garantit en aucun cas le montant final.
    </P>
    <H2>Responsabilité</H2>
    <P>
      L’utilisateur reconnaît que Mon Précieux n’achète, ne vend, ni n’expédie aucun appareil.
      Mon Précieux ne peut être tenu responsable des transactions effectuées sur des plateformes
      tierces.
    </P>
    <H2>Modification des CGU</H2>
    <P>
      Mon Précieux se réserve le droit de modifier les présentes CGU à tout moment. La version
      en vigueur est celle publiée sur cette page.
    </P>
  </Page>
);

export const FAQ = () => (
  <Page eyebrow="✦ Questions fréquentes" title="FAQ">
    <H2>Comment fonctionne Mon Précieux&nbsp;?</H2>
    <P>
      En 1 minute&nbsp;: choisissez votre catégorie, votre modèle et sa capacité, répondez à
      3-4 questions sur l’état de l’appareil. Vous obtenez une estimation immédiate et vous
      pouvez finaliser la reprise directement sur CompaRecycle.
    </P>
    <H2>L’estimation est-elle garantie&nbsp;?</H2>
    <P>
      Non&nbsp;: l’estimation est indicative. Le prix définitif est calculé par CompaRecycle
      après vérification de votre appareil. Notre rôle est de vous donner une fourchette
      fiable en quelques clics, basée sur les barèmes publics CompaRecycle.
    </P>
    <H2>Combien coûte le service&nbsp;?</H2>
    <P>
      Mon Précieux est entièrement gratuit pour l’utilisateur. Aucun frais, aucune commission
      prélevée sur le prix de votre reprise.
    </P>
    <H2>Mon appareil n’apparaît pas, que faire&nbsp;?</H2>
    <P>
      Si votre modèle n’est pas dans la liste, c’est qu’il n’est pas (encore) éligible chez
      CompaRecycle. Vous pouvez nous contacter pour signaler une absence&nbsp;: nous mettons
      le catalogue à jour régulièrement.
    </P>
    <H2>Et si mon écran est cassé&nbsp;?</H2>
    <P>
      Même cassés ou abîmés, la plupart des appareils ont une valeur. Sélectionnez «&nbsp;Cassé /
      fissuré&nbsp;» pour l’écran et l’estimation s’ajustera en conséquence.
    </P>
    <H2>Quand suis-je payé(e)&nbsp;?</H2>
    <P>
      Le paiement est effectué par CompaRecycle ou son partenaire après réception et vérification
      de votre appareil. Le délai varie selon le mode d’expédition choisi sur leur plateforme.
    </P>
  </Page>
);
